import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import { api, money } from "../lib/api";
import { useCart } from "../lib/cart";
import { useSeo } from "../lib/seo";
import { trackGoogleAdsPurchase } from "../lib/analytics";
import { Check, XCircle, Clock } from "lucide-react";

export default function CheckoutResult() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");
  const [status, setStatus] = useState("polling");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const polledRef = React.useRef(false);
  useSeo({ title: "Order Confirmation | Lecis Auto Glass", description: "Order confirmation page.", canonicalPath: "/checkout/success", robots: "noindex,follow" });

  useEffect(() => {
    if (!sessionId || polledRef.current) return;
    polledRef.current = true;
    let cancelled = false;

    const poll = async (attempt = 0) => {
      if (cancelled || attempt >= 8) {
        if (!cancelled) setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          if (orderId) {
            const ord = await api.get(`/orders/${orderId}`);
            setOrder(ord.data.order);
            // Google Ads purchase conversion — only after Stripe payment is
            // verified paid; deduped by order id against refresh/revisit.
            trackGoogleAdsPurchase(ord.data.order);
          }
          return;
        }
        if (data.status === "complete" && data.payment_status === "unpaid") {
          // Async method (e.g. ACH) accepted but still clearing at the bank.
          // The order is placed as pending — do NOT fire the purchase conversion.
          setStatus("processing");
          clear();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch (e) {
        // ignore and retry
      }
      setTimeout(() => poll(attempt + 1), 2000);
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, orderId, clear]);

  return (
    <StorefrontLayout>
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="checkout-result">
        {status === "polling" && (
          <>
            <Clock size={48} className="mx-auto text-zinc-400 mb-6 animate-pulse" />
            <h1 className="font-display text-3xl md:text-4xl mb-3">Confirming payment…</h1>
            <p className="text-zinc-500">Hang tight — we're verifying your transaction with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500 flex items-center justify-center mx-auto mb-6">
              <Check size={28} className="text-emerald-400" />
            </div>
            <h1 className="font-display text-3xl md:text-5xl tracking-tighter mb-3" data-testid="checkout-success-heading">Order confirmed</h1>
            <p className="text-zinc-400 mb-8">Thank you — a confirmation has been sent. We'll dispatch your order within 2 business days.</p>
            {order && (
              <div className="text-left bg-[#0d0d0d] border border-white/10 rounded-md p-6 mb-8">
                <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">Order {order.id.slice(0, 8)}</div>
                <ul className="space-y-2 mb-4">
                  {order.items.map((it) => (
                    <li key={it.product_id} className="flex items-center justify-between text-sm">
                      <span>{it.name} <span className="text-zinc-500">× {it.quantity}</span></span>
                      <span className="font-mono">{money(it.price * it.quantity)}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                  <span className="text-sm uppercase tracking-[0.2em] text-zinc-400">Total</span>
                  <span className="font-mono text-lg">{money(order.subtotal)}</span>
                </div>
              </div>
            )}
            <Link to="/shop" className="inline-block bg-red-600 hover:bg-red-700 px-6 h-12 leading-[3rem] text-white text-sm font-medium tracking-wide rounded-sm">
              Continue shopping
            </Link>
          </>
        )}
        {status === "processing" && (
          <>
            <Clock size={48} className="mx-auto text-amber-400 mb-6" />
            <h1 className="font-display text-3xl md:text-4xl mb-3" data-testid="checkout-processing-heading">Bank payment processing</h1>
            <p className="text-zinc-500 mb-8">Your order is placed and awaiting bank confirmation. ACH / bank payments can take a few business days to clear — we'll email you and begin dispatch as soon as it's confirmed.</p>
            <Link to="/shop" className="border border-white/20 px-6 h-12 leading-[3rem] text-white inline-block rounded-sm">Continue shopping</Link>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle size={48} className="mx-auto text-red-500 mb-6" />
            <h1 className="font-display text-3xl md:text-4xl mb-3">Payment didn't complete</h1>
            <p className="text-zinc-500 mb-8">Your card was not charged. Please try again from your cart.</p>
            <Link to="/cart" className="bg-red-600 hover:bg-red-700 px-6 h-12 leading-[3rem] text-white inline-block rounded-sm">Return to cart</Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <Clock size={48} className="mx-auto text-zinc-400 mb-6" />
            <h1 className="font-display text-3xl md:text-4xl mb-3">Still processing</h1>
            <p className="text-zinc-500 mb-8">This is taking longer than expected. Check your email — we'll confirm your order shortly.</p>
            <Link to="/" className="border border-white/20 px-6 h-12 leading-[3rem] text-white inline-block rounded-sm">Go home</Link>
          </>
        )}
      </div>
    </StorefrontLayout>
  );
}
